import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Calendar, Coffee } from 'lucide-react';
import { personalInfo } from '../mock';

const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
};

const About = () => {
  const [imgRef, imgVisible] = useReveal(0.2);
  const [textRef, textVisible] = useReveal(0.2);

  return (
    <section
      id="about"
      className="portfolio-section"
      style={{ paddingTop: '100px', paddingBottom: '100px' }}
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Image Column */}
          <div
            ref={imgRef}
            style={{
              opacity: imgVisible ? 1 : 0,
              transform: imgVisible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}
          >
            <div className="relative">
              {/* Decorative border offset */}
              <div
                className="absolute"
                style={{
                  inset: 0,
                  border: '2px solid var(--accent)',
                  borderRadius: '22px',
                  transform: 'translate(12px, 12px)',
                  opacity: 0.25,
                  zIndex: 0
                }}
              />
              {/* Image */}
              <div className="profile-image-wrap relative z-10">
                <img
                  src={personalInfo.profileImage}
                  alt={personalInfo.name}
                  style={{
                    width: '100%',
                    aspectRatio: '4/5',
                    objectFit: 'cover',
                    display: 'block',
                    borderRadius: '20px'
                  }}
                  loading="lazy"
                />
                {/* Gradient overlay at bottom */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '35%',
                    background: 'linear-gradient(to top, rgba(9,9,11,0.5), transparent)',
                    borderRadius: '0 0 20px 20px',
                    pointerEvents: 'none'
                  }}
                />
              </div>

              {/* Floating "Experience" card */}
              <div
                className="absolute glass-card flex items-center gap-3 px-4 py-3"
                style={{
                  bottom: '-20px',
                  right: '-10px',
                  zIndex: 20,
                  minWidth: '170px'
                }}
              >
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: 'var(--tag-bg)',
                    border: '1px solid var(--tag-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--accent)',
                    flexShrink: 0
                  }}
                >
                  <Coffee size={16} />
                </div>
                <div>
                  <p
                    className="font-bold"
                    style={{ fontSize: '1.1rem', color: 'var(--text-primary)', lineHeight: 1 }}
                  >
                    7+ Years
                  </p>
                  <p
                    className="text-xs mt-0.5 mono"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    of experience
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Text Column */}
          <div
            ref={textRef}
            className="flex flex-col gap-6"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateX(0)' : 'translateX(40px)',
              transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s'
            }}
          >
            <div>
              <p className="section-label mb-3">About Me</p>
              <h2
                className="font-bold tracking-tight"
                style={{ fontSize: 'clamp(2rem, 3.8vw, 2.9rem)', color: 'var(--text-primary)', lineHeight: 1.12 }}
              >
                Full-stack solutions,{' '}
                <span className="accent-text">built with purpose</span>
              </h2>
            </div>

            <p
              style={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: '0.95rem' }}
            >
              {personalInfo.shortBio}
            </p>

            

            {/* Meta info */}
            <div className="flex flex-col gap-3 mt-2">
              {[
                { icon: MapPin, text: personalInfo.location },
                { icon: Calendar, text: 'Open to full-time & contract roles' },
                { icon: Coffee, text: 'Remote-first' }
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'var(--tag-bg)',
                      border: '1px solid var(--tag-border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--accent)',
                      flexShrink: 0
                    }}
                  >
                    <Icon size={14} />
                  </div>
                  <span
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {text}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex gap-3 mt-2">
              <a
                href={personalInfo.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent gap-2 px-5 py-2.5 text-sm"
                style={{ textDecoration: 'none', border: 'none' }}
              >
                Download Resume
              </a>
              <button
                onClick={() => {
                  const el = document.getElementById('contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-ghost-accent px-5 py-2.5 text-sm"
                style={{ cursor: 'pointer' }}
              >
                Let's Talk
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
